import { InMemoryCacheConfig } from '@apollo/client';
import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';

import { apolloConfig } from '@willow/graphql-iso/src/portal';
import { Auth0Domain } from '@willow/shared-iso';
import { AuthorizedProvider, getAppContext, InitLoader, initSentry, UniversalLoader } from '@willow/shared-web';

import { App } from './App';
import reportWebVitals from './reportWebVitals';

import './index.scss';

const APP_CONTEXT = getAppContext();
const AUTH0_DOMAIN = Auth0Domain.getCustomDomainForPortal(APP_CONTEXT);
const AUTH0_AUDIENCE = `https://${Auth0Domain.getForPortal(APP_CONTEXT)}/api/v2/`;
const GRAPHQL_ENDPOINT =
  process.env.NODE_ENV === 'production' ? '/api/portal/graphql' : 'http://localhost:8000/api/portal/graphql';
const PORTAL_CACHE_CONFIG: InMemoryCacheConfig = {
  typePolicies: {
    PortalLoanSnapshot: {
      keyFields: ['loanId'], // Declare index field so that Apollo merges responses correctly
    },
    Borrower: {
      keyFields: ['id', 'companyId'],
    },
  },
  possibleTypes: apolloConfig.possibleTypes,
};

const isSessionStorageAvailable = () => {
  try {
    const testKey = '__test__';
    window.sessionStorage.setItem(testKey, '1');
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

export const PortalApp = () => {
  // This has to stay above the cookie and session-storage guards below. A hook that sits after an
  // early return is skipped on the renders that take that return, which changes the hook order
  // React sees between renders and corrupts hook state.
  useEffect(() => {
    // If you want to start measuring performance in your app, pass a function
    // to log results (for example: reportWebVitals(console.log))
    // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
    reportWebVitals();
    if (APP_CONTEXT === 'production') {
      initSentry();
    }
  }, []);

  if (!navigator.cookieEnabled) {
    return <>Please enable cookies to view this site</>;
  }
  if (!isSessionStorageAvailable()) {
    return (
      <>
        To use this site, please enable session storage in your browser settings. Session storage is required to
        maintain your login state and preferences. If you're using private/incognito mode, try switching to a regular
        browser window or adjust your browser settings to allow session storage.
      </>
    );
  }

  return (
    <Router>
      <HelmetProvider>
        <QueryParamProvider adapter={ReactRouter5Adapter}>
          {/* eslint-disable-next-line react/no-unstable-nested-components -- TODO: pre-existing, surfaced by removing the NamedMemo wrapper; fix in follow-up */}
          <UniversalLoader loader={(isLoading) => <InitLoader visible={isLoading} />}>
            <AuthorizedProvider
              auth0Domain={AUTH0_DOMAIN}
              auth0ClientId={Auth0Domain.getPortalClientId(APP_CONTEXT)}
              auth0Audience={AUTH0_AUDIENCE}
              graphqlEndpoint={GRAPHQL_ENDPOINT}
              apolloCacheConfig={PORTAL_CACHE_CONFIG}
            >
              <App appContextEnv={APP_CONTEXT} />
            </AuthorizedProvider>
          </UniversalLoader>
        </QueryParamProvider>
      </HelmetProvider>
    </Router>
  );
};
